import { type FC } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { formatCurrency, maskCardNumber, maskMobileNumber } from '@glomopay/utils';

import mockBankLogoImg from '../images/mock-bank-logo.svg';
import mastercardLogoImg from '../images/mastercard-logo.svg';

const otpFormSchema = z.object({
  otp: z.string().regex(/^\d{6}$/, 'OTP must be exactly 6 digits'),
});

type TOtpFormData = z.infer<typeof otpFormSchema>;

interface MockBankPageUIProps {
  cardNumber: string;
  mobileNumber?: string;
  amount: number;
  currency: string;
  correctOtp: string;
  onSubmit: () => void | Promise<void>;
  onCancel: () => void | Promise<void>;
  onClose?: () => void;
}

const MockBankPageUI: FC<MockBankPageUIProps> = ({ cardNumber, mobileNumber, amount, currency, correctOtp, onSubmit, onCancel, onClose }) => {
  const form = useForm<TOtpFormData>({
    resolver: zodResolver(otpFormSchema),
    mode: 'onChange',
  });

  const handleSubmit: SubmitHandler<TOtpFormData> = async (values) => {
    if (values.otp !== correctOtp)
      return form.setError('otp', {
        message: 'Incorrect OTP. Try again',
      });

    try {
      await onSubmit();
      onClose?.();
    } catch (error) {
      console.log(error);
    }
  };

  const handleCancelButtonClick = async () => {
    try {
      await onCancel();
      onClose?.();
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <div className='light' style={{ height: '100dvh', width: '100dvw', display: 'flex', justifyContent: 'center', alignItems: 'center', color: 'CanvasText', backgroundColor: 'var(--bg-default, #fff)' }}>
      {/* Main container */}
      <main
        style={{
          width: '24.5rem',
          filter: 'drop-shadow(0px 4px 8px rgba(0, 0, 0, 0.06)) drop-shadow(0px 0px 4px rgba(0, 0, 0, 0.04))',
          borderRadius: '4px',
          overflow: 'hidden',
        }}>
        {/* Header */}
        <div style={{ padding: '1.25rem 1rem', backgroundColor: '#704193', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <img data-testid='bank-logo' src={mockBankLogoImg} alt='BANK LOGO' />

          <div style={{ backgroundColor: '#fff', padding: '.25rem' }}>
            <img data-testid='mastercard-logo' src={mastercardLogoImg} alt='MASTERCARD LOGO' />
          </div>
        </div>

        {/* Body */}
        <div style={{ padding: '1.25rem', backgroundColor: '#FEFEFE' }}>
          {/* Customer details */}
          <h4 data-testid='customer-details-heading' style={{ color: '#434343', fontWeight: 700, marginBottom: '1rem' }}>
            Customer details
          </h4>

          <div
            style={{
              padding: '.75rem 1.5rem',
              border: '1.5px solid #E7E7E7',
              display: 'grid',
              gridTemplateColumns: 'max-content 1fr',
              textTransform: 'capitalize',
              fontSize: '14px',
              borderRadius: '4px',
              marginBottom: '1.75rem',
              rowGap: '.75rem',
              columnGap: '1rem',
            }}>
            <span style={{ color: '#656363' }}>card number:</span>
            <span data-testid='card-number' style={{ fontWeight: 600 }}>
              {maskCardNumber(cardNumber)}
            </span>

            {mobileNumber && (
              <>
                <span style={{ color: '#656363' }}>mobile number:</span>
                <span data-testid='mobile-number' style={{ fontWeight: 600 }}>
                  {maskMobileNumber(mobileNumber)}
                </span>
              </>
            )}

            <span style={{ color: '#656363' }}>amount:</span>
            <span data-testid='amount' style={{ fontWeight: 600 }}>
              {formatCurrency(amount, currency)}
            </span>
          </div>

          {/* OTP Form */}
          <form onSubmit={form.handleSubmit(handleSubmit)}>
            <h4 data-testid='otp-heading' style={{ color: '#434343', fontWeight: 700, marginBottom: '1.25rem' }}>
              Enter OTP
            </h4>
            <p data-testid='otp-instructions' style={{ marginBottom: '1rem', lineHeight: 1.4 }}>
              An OTP (One Time Password), has been sent to the registered mobile number. Please authenticate the transaction using OTP. <br />
              (OTP: {correctOtp})
            </p>
            {/* Otp input */}
            <div>
              <input
                {...form.register('otp')}
                data-testid='otp-input'
                type='password'
                placeholder='Enter 6 digits for OTP'
                maxLength={6}
                style={{
                  padding: '.75rem 1.5rem',
                  width: '100%',
                  border: '1.5px solid #E7E7E7',
                  borderRadius: '4px',
                  marginBottom: '.2rem',
                  backgroundColor: '#fff',
                }}
              />
              {form.formState.errors.otp && (
                <p data-testid='otp-error' style={{ color: '#F24141', fontSize: '12px' }}>
                  {form.formState.errors.otp.message}
                </p>
              )}
            </div>
            {/* Form buttons */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: '1rem', marginTop: '.75rem', marginBottom: '.625rem' }}>
              <button
                data-testid='cancel-button'
                type='button'
                onClick={handleCancelButtonClick}
                style={{
                  textTransform: 'capitalize',
                  borderRadius: '8px',
                  border: '1px solid #704193',
                  backgroundColor: '#FAF4FF',
                  padding: '.5rem 1rem',
                  color: '#704193',
                }}>
                cancel
              </button>
              <button
                data-testid='submit-button'
                type='submit'
                disabled={!form.formState.isValid || form.formState.isSubmitting}
                style={{
                  textTransform: 'capitalize',
                  borderRadius: '8px',
                  backgroundColor: '#704193',
                  padding: '.5rem 1rem',
                  color: '#FEFEFE',
                }}>
                {form.formState.isSubmitting ? 'submitting...' : 'confirm'}
              </button>
            </div>
          </form>

          <button
            data-testid='resend-otp-button'
            type='button'
            style={{
              color: '#704193',
              textAlign: 'center',
              fontSize: '14px',
              fontWeight: 500,
              display: 'block',
              margin: '0 auto',
              backgroundColor: 'transparent',
            }}>
            Resend OTP
          </button>

          <p data-testid='warning-message' style={{ color: '#898989', fontSize: '12px', fontWeight: 500, textAlign: 'center', marginTop: '3.5rem' }}>
            Do not disclose it to anyone, bank never asks for OTP.
          </p>
        </div>
      </main>
    </div>
  );
};

export default MockBankPageUI;
